import {
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../app/models";

export type TaxEntry = { country: string; value: string };

export function isNoTaxIdReasonCode(value: string): boolean {
  const normalized = value.trim();
  return normalized === "A" || normalized === "B" || normalized === "C";
}

export function isTaxEntry(obj: unknown): obj is TaxEntry {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "country" in obj &&
    "value" in obj &&
    typeof (obj as TaxEntry).country === "string" &&
    typeof (obj as TaxEntry).value === "string"
  );
}

function toTaxEntry(item: unknown, defaultCountry: string): TaxEntry {
  if (isTaxEntry(item)) {
    return item;
  }
  if (typeof item === "object" && item !== null) {
    const country = Object.keys(item)[0] ?? defaultCountry;
    const val = (item as Record<string, unknown>)[country];
    return {
      country,
      value: val != null ? String(val) : "",
    };
  }
  return { country: defaultCountry, value: "" };
}

/**
 * Normalizes any known tax_id format into TaxEntry[].
 * Handles: TaxEntry (single), TaxEntry[], Record<string,string>[] (legacy), JSON string.
 */
export function parseTaxEntries(
  value: unknown,
  defaultCountry: string
): TaxEntry[] {
  if (value == null || value === "") {
    return [{ country: defaultCountry, value: "" }];
  }

  let parsed: unknown;
  if (isTaxEntry(value)) {
    return [value];
  }
  if (Array.isArray(value) && value.length > 0) {
    parsed = value;
  } else if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [{ country: defaultCountry, value: "" }];
    }
  } else {
    return [{ country: defaultCountry, value: "" }];
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [{ country: defaultCountry, value: "" }];
  }

  return parsed.map((item) => toTaxEntry(item, defaultCountry));
}

export function normalizeTaxId(
  value: unknown,
  multiple: boolean
): TaxEntry | TaxEntry[] | undefined {
  let entries: TaxEntry[];
  if (Array.isArray(value)) {
    entries = value.filter(isTaxEntry);
  } else if (isTaxEntry(value)) {
    entries = [value];
  } else {
    return value as TaxEntry | undefined;
  }

  const filled = entries.filter(
    (entry) =>
      entry.value.trim() !== "" && (entry.country || "").trim() !== ""
  );
  if (filled.length === 0) return undefined;
  return multiple ? filled : filled[0];
}

export function cleanupTaxIds(
  values: Record<string, unknown>,
  configFields?: TVerificationAppFieldConfigItem[]
): Record<string, unknown> {
  if (!configFields || configFields.length === 0) {
    return values;
  }

  const result = { ...values };
  for (const field of configFields) {
    if (field.type !== TConfigItemType.TAX_ID) continue;
    if (typeof field.name !== "string") continue;
    if (result[field.name] === undefined) continue;
    result[field.name] = normalizeTaxId(
      result[field.name],
      !!field.multiple
    );
  }

  return result;
}
